<template>
  <section class="featured-deals-redesign fd-compact">
    <div class="fd-header">
      <div>
        <h2 class="fd-title">🔥 Top Featured Deals</h2>
        <p class="fd-subtitle">Handpicked offers just for you. Grab them before they’re gone!</p>
      </div>
      <q-btn to="/featured" color="accent" class="fd-viewall-btn" icon="visibility" label="View All" rounded unelevated no-caps />
    </div>

    <div v-if="loading" class="fd-loading">
      <q-skeleton v-for="i in 4" :key="i" height="220px" width="100%" class="fd-skeleton" square />
    </div>

    <div v-else-if="error" class="fd-error">
      <img src="/images/placeholder.png" alt="Error" class="fd-illustration" />
      <div class="fd-error-text">{{ error }}</div>
      <q-btn label="Try Again" color="accent" @click="$emit('refresh')" class="q-mt-md" rounded no-caps />
    </div>

    <div v-else-if="deals.length === 0" class="fd-empty">
      <q-icon name="local_offer" size="48px" color="grey-4" />
      <div class="fd-empty-text">No featured deals right now. Check back soon for fresh savings!</div>
    </div>

    <div v-else class="fd-grid">
      <div v-for="(deal, idx) in deals" :key="idx" class="fd-card" @click="openCouponLink(deal)">
        <div class="fd-img-wrap">
          <q-img
            :src="deal.featured_image || deal.store?.logo_url || '/images/placeholder.png'"
            :alt="deal.title"
            height="100%"
            width="100%"
            class="fd-img"
            fit="cover"
            @error="onImgError"
          >
            <template v-slot:loading>
              <q-spinner-dots color="accent" />
            </template>
          </q-img>
          <q-badge v-if="deal.discount_value" class="fd-badge" color="accent" rounded>
            {{ deal.discount_value }}{{ deal.discount_type === 'percentage' ? '%' : '' }} OFF
          </q-badge>
        </div>
        <div class="fd-content">
          <div class="fd-store-row">
            <q-avatar v-if="deal.store?.logo_url" size="28px" class="fd-store-avatar">
              <q-img :src="deal.store?.logo_url" :alt="deal.store?.name" fit="contain" />
            </q-avatar>
            <span class="fd-store-name">{{ deal.store?.name }}</span>
          </div>
          <div class="fd-deal-title">{{ deal.title }}</div>
          <div class="fd-deal-desc">{{ deal.description || 'Save with this exclusive offer!' }}</div>
          <div v-if="deal.expire_date" class="fd-expiry">
            <q-icon name="event" size="16px" class="q-mr-xs" color="primary" />
            <span>Expires: {{ formatExpiry(deal.expire_date) }}</span>
          </div>
        </div>
        <q-btn 
          @click.stop="openCouponLink(deal)"
          :color="deal.code ? 'primary' : 'accent'"
          class="fd-action-btn"
          :label="deal.code ? 'Get Code' : 'View Deal'"
          :icon="deal.code ? 'content_copy' : 'shopping_cart'"
          rounded
          no-caps
        />
      </div>
    </div>
  </section>
</template>

<script setup>
const props = defineProps({
  deals: { type: Array, default: () => [] },
  loading: Boolean,
  error: String,
});
const emit = defineEmits(['refresh', 'open-coupon-link', 'img-error']);

const openCouponLink = (deal) => emit('open-coupon-link', deal);
const onImgError = (e) => emit('img-error', e);

const formatExpiry = (date) => {
  if (!date) return 'No expiry';
  const d = new Date(date);
  const diff = Math.ceil((d - new Date()) / (1000 * 60 * 60 * 24));
  if (diff < 0) return 'Expired';
  if (diff === 0) return 'Expires today';
  if (diff === 1) return 'Expires tomorrow';
  if (diff < 30) return `${diff} days left`;
  return d.toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' });
};
</script>

<style scoped>
.featured-deals-redesign {
  background: linear-gradient(135deg, #f8fafc 0%, #e0e7ff 100%);
  border-radius: 12px;
  padding: 1.6rem 0.9rem 1.3rem 0.9rem;
  box-shadow: 0 2px 16px 0 rgba(80, 80, 180, 0.08);
  margin-bottom: 1.6rem;
}
.fd-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 1.4rem;
}
.fd-title {
  font-size: 1.35rem;
  font-weight: 800;
  color: #2d2d6a;
  margin-bottom: 0.1rem;
}
.fd-subtitle {
  color: #5c5c8a;
  font-size: 0.72rem;
  margin-bottom: 0;
}
.fd-viewall-btn {
  font-weight: 600;
  font-size: 0.65rem;
  box-shadow: 0 2px 12px 0 rgba(80, 80, 180, 0.10);
  padding: 0.2em 0.7em;
}
.fd-loading, .fd-error, .fd-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 140px;
  width: 100%;
}
.fd-skeleton {
  margin: 0.3rem 0;
  border-radius: 10px;
  height: 140px !important;
}
.fd-illustration {
  width: 78px;
  margin-bottom: 0.7rem;
  opacity: 0.85;
}
.fd-error-text, .fd-empty-text {
  color: #b71c1c;
  font-size: 0.72rem;
  margin-bottom: 0.4rem;
  text-align: center;
}
.fd-empty-text {
  color: #5c5c8a;
}
.fd-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 0.9rem;
}
.fd-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 16px 0 rgba(80, 80, 180, 0.10);
  transition: transform 0.18s, box-shadow 0.18s;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  min-height: 220px;
}
.fd-card:hover {
  transform: translateY(-4px) scale(1.015);
  box-shadow: 0 4px 16px 0 rgba(80, 80, 180, 0.13);
}
.fd-img-wrap {
  position: relative;
  width: 100%;
  height: 78px;
  background: #f1f5fa;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}
.fd-img {
  object-fit: cover;
  width: 100%;
  height: 100%;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  display: block;
}
.fd-badge {
  position: absolute;
  top: 7px;
  left: 7px;
  font-size: 0.62rem;
  font-weight: 700;
  padding: 0.18em 0.6em;
  background: linear-gradient(90deg, #ffb347 0%, #ff5e62 100%);
  color: #fff;
  box-shadow: 0 2px 8px 0 rgba(255, 94, 98, 0.12);
}
.fd-content {
  padding: 0.7rem 0.7rem 0.4rem 0.7rem;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 0.18rem;
}
.fd-store-row {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  margin-bottom: 0.1rem;
}
.fd-store-avatar {
  background: #fff;
  border: 1px solid #e0e7ff;
}
.fd-store-name {
  font-size: 0.63rem;
  color: #3f51b5;
  font-weight: 600;
}
.fd-deal-title {
  font-size: 0.74rem;
  font-weight: 700;
  color: #2d2d6a;
  margin-bottom: 0.1rem;
  line-height: 1.2;
  min-height: 1.3em;
}
.fd-deal-desc {
  color: #5c5c8a;
  font-size: 0.63rem;
  min-height: 1.2em;
  margin-bottom: 0.1rem;
}
.fd-expiry {
  color: #b71c1c;
  font-size: 0.6rem;
  margin-top: 0.1rem;
  display: flex;
  align-items: center;
}
.fd-action-btn {
  margin: 0 0.7rem 0.7rem 0.7rem;
  font-weight: 700;
  font-size: 0.66rem;
  letter-spacing: 0.01em;
  box-shadow: 0 2px 8px 0 rgba(80, 80, 180, 0.10);
  transition: background 0.18s;
  padding: 0.2em 0.7em;
}
.fd-action-btn:hover {
  background: linear-gradient(90deg, #ffb347 0%, #ff5e62 100%) !important;
  color: #fff !important;
}
@media (max-width: 700px) {
  .featured-deals-redesign {
    padding: 0.8rem 0.15rem 0.8rem 0.15rem;
  }
  .fd-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.4rem;
  }
  .fd-title {
    font-size: 0.9rem;
  }
  .fd-grid {
    gap: 0.4rem;
  }
  .fd-card {
    min-height: 140px;
  }
}
</style>
